import { memo, useCallback, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { createId } from '@paralleldrive/cuid2';
import type { BrandInsightsInstanceContext } from '@trinity/chat';
import { ChatContextType, KolSearchStatusEnum } from '@trinity/chat';
import { agentResponseJsonDataSchema, LandingCardActionTypeEnum } from '@trinity/management-app-client';
import { ChevronRight } from 'react-feather';
import { useIsFullPageChat } from '@trinity/chat-components/components/Chat/hooks/useIsFullPageChat';
import { useSubmitQueryMutation } from '@trinity/chat-components/components/Chat/hooks/useSubmitQueryMutation';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { buildDefaultChatContext } from '@trinity/chat-components/components/Chat.State/state.initial-state';
import { LandingCardIconRenderer } from '@trinity/chat-components/components/LandingCardIcon/LandingCardIconRenderer.js';
import { useNavigate } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import type { ChatState } from '../Chat.State/interfaces.chat';
import { ActivePageContext, ChatContextItemsContext, ChatLandingCardsContext } from './context';
import type { LandingCard } from './interfaces.context';
import styles from './style.module.css';

interface LandingCardProps {
  title: string | null;
  description: string | null;
  icon: string | null;
  onClick: () => void;
}

interface PromptInjectionActionData {
  prompt: string;
}

interface RedirectActionData {
  url: string;
}

interface DispatchActionData {
  contextOverride?: BrandInsightsInstanceContext.ItemWithMetadata;
}

export const LandingCards = memo(() => {
  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const navigate = useNavigate();
  const submitQueryMutation = useSubmitQueryMutation();
  const chatContexts = useContext(ChatContextItemsContext);
  const { setPage } = useContext(ActivePageContext);

  const cards = useContext(ChatLandingCardsContext);

  const dispatchActions = useCallback(
    (actions: ChatState.BaseAction[]) => {
      dispatch({
        type: 'batch-actions',
        payload: {
          actions: [
            ...actions,
            {
              type: 'landing-cards-visibility-toggled',
              payload: { visible: false },
            },
          ],
        },
      });
    },
    [dispatch]
  );

  // Action handlers
  const handleDispatchAction = useCallback(
    (card: LandingCard) => {
      return () => {
        if (!card.chatContext || !chatContexts) return;
        const actionData = card.actionData as DispatchActionData;

        const fullContext = chatContexts.find((ctx) => ctx.identifier === card.chatContext.identifier);
        if (!fullContext) return;

        // Use the same logic as Chat.Agents.Container
        const profileAgent = fullContext.agent.identifier === 'kol-profile';
        const globalAgent = fullContext.agent.identifier === 'global';

        let context: BrandInsightsInstanceContext.ItemWithMetadata = {
          type: ChatContextType.Agent,
          data: fullContext,
        };
        if (actionData?.contextOverride) {
          context = actionData.contextOverride;
        } else if (profileAgent) {
          context = buildDefaultChatContext(ChatContextType.KolProfile);
        } else if (globalAgent) {
          context = buildDefaultChatContext(ChatContextType.Global);
        }

        dispatchActions([
          {
            type: 'instance-context-changed',
            payload: {
              context,
              brandIdentifier,
            },
          },
          {
            type: 'default-context-updated',
            payload: {
              defaultContext: context,
            },
          },
        ]);

        setPage(fullContext.pageId ?? null);
      };
    },
    [dispatchActions, brandIdentifier, chatContexts, setPage]
  );

  const handlePromptInjectionAction = useCallback(
    (card: LandingCard) => {
      return () => {
        if (!card.chatContext || !chatContexts) return;

        const fullContext = chatContexts.find((ctx) => ctx.identifier === card.chatContext.identifier);
        if (!fullContext) return;

        const profileAgent = fullContext.agent.identifier === 'kol-profile';
        const globalAgent = fullContext.agent.identifier === 'global';

        const context: BrandInsightsInstanceContext.ItemWithMetadata = profileAgent
          ? buildDefaultChatContext(ChatContextType.KolProfile)
          : globalAgent
            ? buildDefaultChatContext(ChatContextType.Global)
            : {
                type: ChatContextType.Agent,
                data: fullContext,
              };

        dispatchActions([
          {
            type: 'instance-context-changed',
            payload: {
              context,
              brandIdentifier,
            },
          },
          {
            type: 'default-context-updated',
            payload: {
              defaultContext: context,
            },
          },
        ]);

        const promptData = card.actionData as PromptInjectionActionData;
        if (promptData?.prompt) {
          const queryIdentifier = createId();

          submitQueryMutation.mutate({
            identifier: queryIdentifier,
            parentQueryIdentifier: null,
            query: promptData.prompt,
            action: null,
            context: context,
          });
        }
      };
    },
    [dispatchActions, brandIdentifier, chatContexts, submitQueryMutation]
  );

  const handleQuestionBankClick = useCallback(() => {
    dispatchActions([
      {
        type: 'kol-search-added',
        payload: {
          item: {
            id: null,
            createdOn: new Date(),
            status: KolSearchStatusEnum.QuestionsBank,
            identifier: createId(),
            followupQuestions: [],
            form: {
              name: '',
            },
            results: null,
            profile: null,
            recommendations: {
              status: 'skipped',
              items: [],
              total: 0,
            },
            citations: [],
          },
        },
      },
    ]);
  }, [dispatchActions]);

  const handleSearchClick = useCallback(() => {
    dispatchActions([
      {
        type: 'kol-search-added',
        payload: {
          item: {
            id: null,
            createdOn: new Date(),
            status: KolSearchStatusEnum.FormEntry,
            identifier: createId(),
            followupQuestions: [],
            form: {
              name: '',
            },
            results: null,
            profile: null,
            recommendations: {
              status: 'skipped',
              items: [],
              total: 0,
            },
            citations: [],
          },
        },
      },
    ]);
  }, [dispatchActions]);

  const handleRecommendationsClick = useCallback(() => {
    dispatchActions([
      {
        type: 'kol-search-added',
        payload: {
          item: {
            id: null,
            createdOn: new Date(),
            status: KolSearchStatusEnum.ProfileRecommendations,
            identifier: createId(),
            followupQuestions: [],
            form: {
              name: '',
            },
            results: null,
            profile: null,
            recommendations: {
              status: 'uninitialized',
              items: [],
              total: 0,
            },
            citations: [],
          },
        },
      },
    ]);
  }, [dispatchActions]);

  type CustomActionData = {
    function: string;
  };

  const handleCustomAction = useCallback(
    (card: LandingCard) => {
      return () => {
        const customActionData = card.actionData as CustomActionData;
        switch (customActionData.function) {
          case '2':
            handleQuestionBankClick();
            break;
          case '3':
            handleRecommendationsClick();
            break;
          case '4':
            handleSearchClick();
            break;
          default:
        }
      };
    },
    [handleQuestionBankClick, handleRecommendationsClick, handleSearchClick]
  );

  const handleRedirectAction = useCallback(
    (card: LandingCard) => {
      return () => {
        const redirectData = card.actionData as RedirectActionData;
        //handle external vs internal links
        if (redirectData?.url) {
          if (redirectData.url.startsWith('http://') || redirectData.url.startsWith('https://')) {
            window.location.href = redirectData.url;
          } else {
            navigate(redirectData.url);
          }
        }
      };
    },
    [navigate]
  );

  const handleAgentResponseAction = useCallback(
    (card: LandingCard) => {
      return () => {
        if (!chatContexts) return;

        const agentResponseData = card.actionData;
        if (!agentResponseData) return;

        // Parse and validate the actionData using the shared schema
        const parseResult = agentResponseJsonDataSchema.safeParse(agentResponseData);

        if (!parseResult.success) {
          return;
        }

        const { subject } = parseResult.data;

        const subjectId = Number(subject.id);

        const currentContext = chatContexts[0];
        if (!currentContext) return;

        const contextWithSubject: BrandInsightsInstanceContext.ItemWithMetadata = {
          type: ChatContextType.Agent,
          data: {
            ...currentContext,
            subject: {
              id: subjectId,
              name: subject.name,
            },
          },
        };

        // Set the context without submitting a query
        dispatch({
          type: 'batch-actions',
          payload: {
            actions: [
              {
                type: 'instance-context-changed',
                payload: {
                  context: contextWithSubject,
                  brandIdentifier,
                },
              },
              {
                type: 'default-context-updated',
                payload: {
                  defaultContext: contextWithSubject,
                },
              },
            ],
          },
        });

        setPage(currentContext.pageId ?? null);
      };
    },
    [chatContexts, dispatch, brandIdentifier, setPage]
  );

  const cardProps = useMemo(() => {
    if (!cards || !Array.isArray(cards)) return [];

    return cards
      .sort((a, b) => a.order - b.order)
      .map((card): LandingCardProps => {
        let onClick: () => void;

        switch (card.actionType) {
          case LandingCardActionTypeEnum.Dispatch:
            onClick = handleDispatchAction(card);
            break;
          case LandingCardActionTypeEnum.PromptInjection:
            onClick = handlePromptInjectionAction(card);
            break;
          case LandingCardActionTypeEnum.Redirect:
            onClick = handleRedirectAction(card);
            break;
          case LandingCardActionTypeEnum.PassResponseDirectlyToAgent:
            onClick = handleAgentResponseAction(card);
            break;
          default:
            onClick = handleCustomAction(card);
            break;
        }

        return {
          title: card.title,
          description: card.description,
          icon: card.icon,
          onClick,
        } as LandingCardProps;
      });
  }, [
    cards,
    handleDispatchAction,
    handlePromptInjectionAction,
    handleRedirectAction,
    handleAgentResponseAction,
    handleCustomAction,
  ]);

  const isFullPageChat = useIsFullPageChat();
  //style card rows per design
  const getCardRows = (cards: LandingCardProps[]) => {
    const count = cards.length;

    // In modal view, show up to 8 cards in 4 rows with 2 cards each
    if (!isFullPageChat) {
      const modalCards = cards.slice(0, 8);
      const rows = [];
      for (let i = 0; i < modalCards.length; i += 2) {
        rows.push(modalCards.slice(i, i + 2));
      }
      return rows;
    }

    // Full page chat - show all cards with original layout
    if (count <= 4) {
      return [cards];
    }

    switch (count) {
      case 5:
        return [cards.slice(0, 3), cards.slice(3, 5)]; // 3 + 2
      case 6:
        return [cards.slice(0, 3), cards.slice(3, 6)]; // 3 + 3
      case 7:
        return [cards.slice(0, 4), cards.slice(4, 7)]; // 4 + 3
      case 8:
      default:
        return [cards.slice(0, 4), cards.slice(4, 8)]; // 4 + 4
    }
  };

  const cardRows = getCardRows(cardProps);

  return (
    <Root className={styles.cards}>
      {cardRows.map((row, rowIndex) => (
        <CardRow key={rowIndex}>
          {row.map((card, cardIndex) => (
            <LandingCardComponent
              key={`${rowIndex}-${cardIndex}`}
              title={card.title}
              description={card.description}
              icon={card.icon}
              onClick={card.onClick}
            />
          ))}
        </CardRow>
      ))}
    </Root>
  );
});

LandingCards.displayName = 'LandingCards';

const LandingCardComponent = memo(({ title, description, icon, onClick }: LandingCardProps) => {
  const isFullPageChat = useIsFullPageChat();

  return (
    <CardRoot
      className={!isFullPageChat ? styles.card : undefined}
      onClick={onClick}
      isFullPageChat={isFullPageChat}
    >
      <div>
        <CardHeader>
          {icon && <LandingCardIconRenderer icon={icon} alt={title || 'Card icon'} width={40} height={40} />}
        </CardHeader>

        <CardTitle className={!isFullPageChat ? styles.title : undefined}>{title}</CardTitle>

        {isFullPageChat && <CardCopy>{description}</CardCopy>}
      </div>

      <CardFooter>
        <CardLink>
          <StyledChevronRightIcon size={20} />
        </CardLink>
      </CardFooter>
    </CardRoot>
  );
});

LandingCardComponent.displayName = 'LandingCardComponent';

const Root = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
  margin: '0 auto',

  '@media (min-width: 800px)': {
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
});

const CardRow = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',

  '@media (min-width: 800px)': {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '15px',
  },
});

const CardRoot = styled.div<{ isFullPageChat: boolean }>(({ theme, isFullPageChat }) => ({
  padding: 15,
  borderRadius: 8,
  backgroundColor: theme.palette.white.main,
  border: `2px solid ${theme.palette.gray.light4}`,
  cursor: 'pointer',

  width: 258,
  height: isFullPageChat ? 280 : 180, // Shorter height for modal view

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'space-between',

  '&:hover': {
    backgroundColor: theme.isTrinityThemed
      ? theme.palette.sentiment.primary.light
      : theme.palette.btnPrimary.bg,
    border: `2px solid ${theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main}`,
  },

  [`&:hover ${CardLink}`]: {
    backgroundColor: theme.isTrinityThemed
      ? theme.palette.sentiment.primary.main
      : theme.palette.primary.main,
  },

  [`&:hover ${StyledChevronRightIcon}`]: {
    transform: 'rotate(90deg)',
    color: theme?.isTrinityThemed ? theme.palette.white.main : theme.palette?.wl?.primary?.text,
  },
}));

const CardHeader = styled.div(() => ({
  display: 'flex',
  justifyContent: 'flex-start',
  marginBottom: 10,
}));

const CardTitle = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 18,
  marginTop: 10,
  height: 63,
}));

const CardCopy = styled.div({});

const CardFooter = styled.div({
  display: 'flex',
  justifyContent: 'center',
});

const CardLink = styled.div(() => ({
  borderRadius: '50%',
  padding: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
}));

const StyledChevronRightIcon = styled(ChevronRight)(({ theme }) => ({
  color: theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main,
  paddingLeft: 2,
  strokeWidth: 3,
  transition: 'transform 0.3s',
}));
